import React, { useState, useEffect, useRef } from "react"
import { Layout, Drawer, Modal, Alert, Button } from "antd"
import styled from "styled-components"
import { useScreenSize, IUser } from "./utils"
import { useHistory, useLocation } from "react-router-dom"
import Sidebar from "./Sidebar"
import EditUserProfile from "./EditUserProfile"
import { screenSizes } from "../styles/GlobalStyles"
import AppHeader from "./AppHeader"
import WebsitesInvitesAlert from "./websites/WebsitesInvitesAlert"
import { ClubSeasonManagementContextProvider } from "./ClubSeasonManagement/sharedState"
import ClubCommunicationDrawer from "./ClubCommunicationDrawer"
import ActivityFeedDrawer from "./ActivityFeedDrawer"
import NewAnnouncementWindow from "./NewAnnouncementWindow"
import { getSidebarWidth } from "./Sidebar/SidebarComponents"
import { useClubSeasonManagementContext } from "./ClubSeasonManagement/sharedState"
import { useStoreState, StateMapper } from "easy-peasy"
import { AppDataStore } from "../appData/types"
import { RoleHelper } from "./utils"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTriangleExclamation, faArrowRight } from "@fortawesome/pro-regular-svg-icons"
import WebsitesPaymentsNewAlert from "./websites/WebsitesPaymentsNewAlert"
import WebsitesOnboardingNewAlert from "./websites/WebsitesOnboardingNewAlert"
import { FeatureAccessProvider } from "./FeatureAccessContext"

const { Header, Sider, Content } = Layout
const AppBody = styled.div`
  min-height: var(--app-viewport-height, 100vh);
  width: 100%;

  @media only screen and (max-width: ${screenSizes.small}px) {
    min-height: var(--app-viewport-height, 100vh);
    overflow: visible;
  }
`

const PageContainer = styled.div`
  max-width: auto;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  padding: 0;

  &.full-bleed {
    max-width: none;
    padding: 0;
  }
`

interface ILayoutProps {
  user: IUser | null
  children: React.ReactNode
}

function UpcomingSeasonAlertBanner() {
  const history = useHistory()
  const { pathname } = useLocation()
  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const impersonateClub = useStoreState((state: StateMapper<AppDataStore>) => state.aliasedClub)
  const roleHelper = new RoleHelper(user?.role)
  const { upcomingSeason } = useClubSeasonManagementContext()
  const screenSize = useScreenSize()
  const isMobile: boolean = screenSize === "small"

  const isOnSeasonRegistrationPage = pathname === "/app/admin/club/seasons"

  const shouldShow =
    Boolean(upcomingSeason) &&
    !Boolean(upcomingSeason?.seasonSetupConfirmedBy) &&
    roleHelper.isAnyAdmin()

  if (!shouldShow) return null

  const bannerCaption = isOnSeasonRegistrationPage
    ? "Select your season below and complete registration"
    : `${upcomingSeason?.seasonName || "Your season"} - Complete season registration setup to open season for ${impersonateClub?.nameLong || "your club"}.`

  const descriptionContent = (
    <span>
      <span role="img" aria-label="Swimming emoji">
        🏊‍♀️
      </span>
      &nbsp;&nbsp;
      {bannerCaption}
      &nbsp;&nbsp;
      <span role="img" aria-label="Swimming emoji">
        🏊‍♂️
      </span>
    </span>
  )

  const desktopAction = (
    <Button
      type="primary"
      size="middle"
      style={{
        backgroundColor: "var(--primaryblue, #25bfea)",
        borderColor: "var(--primaryblue, #25bfea)",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
      }}
      onClick={() => history.push("/app/admin/club/seasons")}
    >
      Complete season setup
      <FontAwesomeIcon icon={faArrowRight} />
    </Button>
  )

  const mobileDescription = (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {descriptionContent}
      <Button
        type="primary"
        size="middle"
        style={{
          backgroundColor: "var(--primaryblue, #25bfea)",
          borderColor: "var(--primaryblue, #25bfea)",
          width: "100%",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
        onClick={() => history.push("/app/admin/club/seasons")}
      >
        Complete season setup
        <FontAwesomeIcon icon={faArrowRight} />
      </Button>
    </div>
  )

  return (
    <div style={{ padding: "0px 12px", marginBottom: 16 }}>
      <Alert
        type="warning"
        icon={<FontAwesomeIcon icon={faTriangleExclamation} style={{ color: "#fa8c16" }} />}
        showIcon
        title="Complete your upcoming season setup"
        description={isMobile ? (isOnSeasonRegistrationPage ? descriptionContent : mobileDescription) : descriptionContent}
        action={!isOnSeasonRegistrationPage && !isMobile ? desktopAction : undefined}
      />
    </div>
  )
}

export default function DashboardLayout({ user, children }: ILayoutProps) {
  const screenSize = useScreenSize()
  const [editProfileVisible, setEditProfileVisible] = useState<boolean>(false)
  const [activityFeedDrawerVisible, setActivityFeedDrawerVisible] = useState<boolean>(false)
  const [clubCommunicationDrawerVisible, setClubCommunicationVisible] = useState<boolean>(false)
  const [messageWindowVisible, setMessageWindowVisible] = useState(false)
  const [userLockout, setUserLockout] = useState(false)

  const isMobile: boolean = screenSize === "small"
  const [isClosed, setIsClosed] = useState<boolean>(isMobile)
  const prevIsMobileRef = useRef(isMobile)

  useEffect(() => {
    if (isMobile && !prevIsMobileRef.current && !isClosed) {
      setIsClosed(true)
    } else if (!isMobile && prevIsMobileRef.current && isClosed) {
      setIsClosed(false)
    }
    prevIsMobileRef.current = isClosed
  }, [isMobile, isClosed])

  const { pathname } = useLocation()

  return (
    <FeatureAccessProvider>
    <ClubSeasonManagementContextProvider>
      <AppBody>
        <NewAnnouncementWindow
          messageWindowVisible={messageWindowVisible}
          setMessageWindowVisible={setMessageWindowVisible}
        />

        <Layout
          className="app-root-layout"
          style={{ minHeight: "var(--app-viewport-height, 100vh)" }}
        >
          {/* Header wrapper reserves header height (reads from CSS var set by AppHeader) */}
          <Header style={{ height: "var(--app-header-height, 64px)", padding: 0 }}>
            <AppHeader
              setEditUserVisible={setEditProfileVisible}
              setActivityFeedVisible={setActivityFeedDrawerVisible}
              setClubCommunicationVisible={setClubCommunicationVisible}
              setSidebarClosed={setIsClosed}
              sidebarClosed={isClosed}
            />
          </Header>

          <Layout className="app-shell-layout">
            {/* Desktop Sider — always expanded, sits under header using CSS var */}
            {user && !isMobile && (
              <Sider
                width={getSidebarWidth(false)}
                className="app-sider"
                style={{
                  background: "var(--primaryblue, #001529)",
                  overflow: "hidden",
                  position: "fixed",
                  left: 0,
                  top: "var(--app-header-height, 64px)", // align under header
                  height:
                    "calc(var(--app-viewport-height, 100vh) - var(--app-header-height, 64px))",
                  zIndex: 1100,
                }}
              >
                <Sidebar
                  isMobile={false}
                  isClosed={false}
                  changeClose={setIsClosed}
                  setUserLockout={setUserLockout}
                />
              </Sider>
            )}

            {/* Mobile Drawer overlay - fill full viewport and let Sidebar handle internal scrolling */}
            {user && isMobile && (
              <Drawer
                open={!isClosed}
                onClose={() => setIsClosed(true)}
                placement="left"
                closable={false}
                zIndex={2000}                                    // ensure drawer sits above mask and header
                styles={{
                  body: {
                    padding: 0,
                    height: "var(--app-viewport-height, 100vh)",
                    background: "var(--primaryblue, #001529)",
                  }, // opaque body
                  mask: { background: "rgba(0,0,0,0.45)", zIndex: 1 },
                }}
                style={{ height: "var(--app-viewport-height, 100vh)", top: 0 }}
                size={getSidebarWidth(false)}
                getContainer={() => document.body}               // ensure portal to <body>
                className="mobile-sidebar-drawer"
              >
                <Sidebar
                  isMobile={true}
                  isClosed={false}
                  changeClose={setIsClosed}
                  setUserLockout={setUserLockout}
                />
              </Drawer>
            )}

            <Content
              style={{
                backgroundColor: "#ffffff",
                padding: 0,
                minHeight:
                  "calc(var(--app-viewport-height, 100vh) - var(--app-header-height, 64px))",
                marginLeft: user && !isMobile ? getSidebarWidth(false) : 0,
                transition: "margin-left 160ms ease",
                // On desktop, keep the elevated card feel; on mobile, go full-bleed under Safari chrome.
                ...(isMobile
                  ? {
                      borderRadius: 0,
                      boxShadow: "none",
                      paddingTop: 12,
                    }
                  : {
                      borderRadius: "20px 0 0 0",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.04)",
                      paddingTop: 24,
                    }),
              }}
            >
              {/* Upcoming season admin banner at top of white content card */}
              <UpcomingSeasonAlertBanner />
              <WebsitesPaymentsNewAlert />
              <WebsitesOnboardingNewAlert />

              {user && <EditUserProfile visible={editProfileVisible} setVisible={setEditProfileVisible} />}
              {user && (
                <ActivityFeedDrawer
                  visible={activityFeedDrawerVisible}
                  setVisible={setActivityFeedDrawerVisible}
                />
              )}
              {user && (
                <ClubCommunicationDrawer
                  visible={clubCommunicationDrawerVisible}
                  setVisible={setClubCommunicationVisible}
                  user={user}
                  setMessageWindowVisible={setMessageWindowVisible}
                />
              )}

              <PageContainer className={pathname === "/app/landingPage" ? "full-bleed" : ""}>
                <section
                  className="content-body"
                  style={{
                    width: "100%",
                    maxWidth: "100%",
                    // On mobile, remove padding so scrollable content can behave like a full-bleed feed
                    // right up to the Safari bottom bar.
                    padding: isMobile ? "0" : undefined,
                  }}
                >
                  {user && <WebsitesInvitesAlert />}
                  {children}
                </section>
              </PageContainer>
            </Content>
          </Layout>

        </Layout>

        <Modal
          title="Cannot access"
          open={userLockout}
          onOk={() => setUserLockout(false)}
          onCancel={() => setUserLockout(false)}
        >
          <div className="form-group mt-lg">
            You cannot access this tab until you have been approved by a user with
            appropriate credentials.
          </div>
        </Modal>
      </AppBody>
    </ClubSeasonManagementContextProvider>
    </FeatureAccessProvider>
  )
}